<template>
  <div v-if="(hasDiscountCode && checkoutStore.event.has_vouchercodes == 1) || giftCardShown">
    <!-- Discount Code / Gift Card Input -->
    <!-- Only show giftcard if any item in cartStore has giftcard_allowed -->
    <BaseButton
      class="md:hidden my-4"
      size="sm"
      color="default"
      @click="toggleVouchercodeInput"
      v-if="checkoutStore.event.has_vouchercodes == 1 && checkoutStore.event.voucher_code_add_description && checkoutStore.event.voucher_code_add_description.length > 0"
    >
      {{
        $t(checkoutStore.event.voucher_code_add_description)
      }}
    </BaseButton>
    <BaseButton
      class="md:hidden my-4"
      size="sm"
      color="default"
      @click="toggleGiftcardInput"
      v-if="cartStore.items.length > 0 && giftCardShown"
    >
      {{
        $t('checkout.redeem_gift_card')
      }}
    </BaseButton>

    <BaseSlideTransition>
      <div v-if="vouchercodeInputOpen">
        <div class="pb-6">
          <form class="gap-4 flex" @submit.prevent="handleSubmit" v-if="!checkoutStore.vouchercode || !checkoutStore.vouchercode.code || checkoutStore.vouchercode.code.length === 0">
            <InputText
              :id="'discount-code'"
              :name="'discountCode'"
              :placeholder="`${$t('checkout.type')} ${$t(checkoutStore.event.voucher_code_description)}`"
              v-model="code"
              class="flex-1"
            />
            <BaseButton size="lg" color="accent" type="submit">{{
              $t('checkout.redeem')
            }}</BaseButton>
          </form>

          <div v-if="checkoutStore.vouchercode && checkoutStore.vouchercode.code && checkoutStore.vouchercode.code.length > 0" class="mt-2 flex flex-wrap gap-2">
            <div
              class="inline-flex items-center gap-2 px-2 py-1 border rounded-sm border-surface-grey font-semibold uppercase bg-surface-grey-light text-sm text-text"
            >
              <TagIcon class="w-5 h-5 fill-current" />
              <span>{{ checkoutStore.vouchercode.code }}</span>
              <button
                type="button"
                @click="removeVouchercode"
                class="hover:opacity-50 cursor-pointer"
              >
                <CloseIcon class="w-5 h-5 fill-current" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="giftcardInputOpen">
        <div class="pb-6">
          <form class="gap-4 flex" @submit.prevent="handleSubmit">
            <InputText
              :id="'gift-card-code'"
              :name="'giftCardCode'"
              :placeholder="`${$t('checkout.type')} ${$t('checkout.gift_card')}`"
              v-model="code"
              class="flex-1"
            />
            <BaseButton size="lg" color="accent" type="submit">{{
              $t('checkout.redeem')
            }}</BaseButton>
          </form>

          <div v-if="giftcardCodes.length > 0" class="mt-2 flex flex-wrap gap-2">
            <div
              v-for="activeCode in giftcardCodes"
              :key="activeCode.code"
              class="inline-flex items-center gap-2 px-2 py-1 border rounded-sm border-surface-grey font-semibold uppercase bg-surface-grey-light text-sm text-text"
            >
              <TagIcon class="w-5 h-5 fill-current" />
              <span>{{ activeCode.code }}</span>
              <button
                type="button"
                @click="removeCode(activeCode.code)"
                class="hover:opacity-50 cursor-pointer"
                :title="$t('common.remove')"
              >
                <CloseIcon class="w-5 h-5 fill-current" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </BaseSlideTransition>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue';
  import { useRouter } from 'vue-router';

  import { useCartStore } from '@/stores/cart';
  import { useCheckoutStore } from '@/stores/checkoutStore';
  import { useToastStore } from '@/stores/toastStore';
  import type { CodeItem } from '@/types';
  import {
    DISCOUNT_STORAGE_KEY,
    GIFTCARD_STORAGE_KEY,
    loadCodesFromStorage,
  } from '@/utils/loadCodesFromLocalStorage';
  import { useWebsocketStore } from '@/websocket/websocket';

  import CloseIcon from '../icons/CloseIcon.vue';
  import TagIcon from '../icons/TagIcon.vue';
  import BaseButton from '../ui/BaseButton.vue';
  import BaseSlideTransition from '../ui/BaseSlideTransition.vue';
  import InputText from '../ui/inputs/InputText.vue';

  const toastStore = useToastStore();
  const websocketStore = useWebsocketStore();
  const checkoutStore = useCheckoutStore();
  const cartStore = useCartStore();
  const router = useRouter();

  const code = ref('');
  const activeCodes = ref<CodeItem[]>([]);

  const props = defineProps<{
    hasDiscountCode?: boolean;
    hasGiftCard?: boolean;
  }>();

  //Giftcard shown if prop enabled and any item allows giftcards
  const giftCardShown = computed(() => {
    return (
      props.hasGiftCard &&
      cartStore.items.some(item => item.giftcard_allowed)
    );
  });
  const vouchercodeInputOpen = ref(false);
  const giftcardInputOpen = ref(false);
  const route = router.currentRoute.value.name;

  if(route == 'checkout-tickets' && checkoutStore.vouchercode && checkoutStore.vouchercode.code && checkoutStore.vouchercode.code.length > 0) {
    vouchercodeInputOpen.value = true;
  }

  // Open the input when a vouchercode is applied (e.g. auto-applied from URL param)
  watch(() => checkoutStore.vouchercode?.code, (code) => {
    if (code && code.length > 0) {
      vouchercodeInputOpen.value = true;
    }
  });

  function toggleVouchercodeInput() {
    vouchercodeInputOpen.value = !vouchercodeInputOpen.value;
  }

  function toggleGiftcardInput() {
    giftcardInputOpen.value = !giftcardInputOpen.value;
  }

  defineExpose({
    toggleVouchercodeInput,
    toggleGiftcardInput,
  });

  const emit = defineEmits(['onChange']);

  function getStorageKey() {
    return props.hasDiscountCode ? DISCOUNT_STORAGE_KEY : GIFTCARD_STORAGE_KEY;
  }

  function saveCodestoStorage() {
    try {
      const storageKey = getStorageKey();
      localStorage.setItem(storageKey, JSON.stringify(activeCodes.value));
      emit('onChange', activeCodes.value);
    } catch (error) {
      console.error('Error saving codes to localStorage:', error);
    }
  }

 /* function validateCode(
    codeToValidate: string,
    type: 'discount' | 'giftcard'
  ): boolean {
    const codeList = type === 'discount' ? discountCodes : giftCodes;
    return codeList.some(
      item => item.code.toLowerCase() === codeToValidate.toLowerCase()
    );
  }*/

  function handleSubmit() {
    if (!code.value.trim()) return;

    const trimmedCode = code.value.trim();

    const codeType: 'discount' | 'giftcard' = props.hasDiscountCode
      ? 'discount'
      : 'giftcard';

    if(!codeType) {
      toastStore.showToast('Ugyldig kode type.');
      code.value = '';
      return;
    }
    else if (codeType === 'discount') {
      handleVouchercodeSubmission(trimmedCode);
      code.value = '';
      return;
    }

    // Giftcard submission
    if (cartStore.giftcardAlreadyAdded(trimmedCode)) {
      toastStore.showToast('Dette gavekort er allerede indløst.');
      code.value = '';
      return;
    }

    if (!cartStore.giftcardAllowed()) {
      toastStore.showToast('Gavekort kan ikke bruges til denne ordre.');
      code.value = '';
      return;
    }

    const vat = cartStore.getGiftcardVat();
    websocketStore.send('giftcard', { code: trimmedCode, vat: vat }, undefined);
    code.value = '';
  }

  function handleVouchercodeSubmission(voucherCode: string) {
    websocketStore.send('vouchercode', { code: voucherCode }, undefined);
  }

  function removeVouchercode() {
    websocketStore.send('vouchercode', { code: '' }, undefined);
  }

  function removeCode(codeToRemove: string) {
    const gc = cartStore.giftcards.find(g => g.code === codeToRemove);
    if (gc) {
      cartStore.removeGiftcard(gc.id);
    }
  }

  // For giftcards, activeCodes is derived from the cart store
  const giftcardCodes = computed<CodeItem[]>(() =>
    cartStore.giftcards.map(gc => ({ code: gc.code, type: 'giftcard' as const }))
  );

  onMounted(() => {
    if (props.hasDiscountCode) {
      activeCodes.value = loadCodesFromStorage(getStorageKey());
      if (activeCodes.value.length > 0) {
        vouchercodeInputOpen.value = true;
      }
    }
    if (cartStore.giftcards.length > 0) {
      giftcardInputOpen.value = true;
    }
  });
</script>
